<template>
  <q-dialog v-model="open" persistent class="windowed-dialog">
    <q-card flat bordered class="q-pa-sm" style="min-width: 340px; max-width: 520px">
      <q-card-section class="row items-center no-wrap" style="padding-bottom: 0">
        <div class="text-subtitle2 ellipsis">{{ headerTitle }}</div>
        <q-space />
        <q-badge color="primary" class="q-mr-sm">Ringing</q-badge>
        <q-btn dense flat round icon="close" @click="onDecline" />
      </q-card-section>

      <q-card-section class="q-pt-none">
        <div class="column items-center justify-center q-gutter-md text-center">
          <UserAvatarBadge :avatar="avatarSrc" size="lg" />
          <div class="text-h6">Incoming call from {{ fromName }}</div>
          <div class="text-caption text-grey-7">{{ conversationLabel }}</div>
          <div class="text-caption q-mt-sm">Adventurers, gather round...</div>
          <div class="row items-center justify-center q-gutter-md q-mt-md">
            <q-btn
              round
              color="positive"
              icon="call"
              size="lg"
              class="animate__animated animate__infinite animate__pulse"
              @click="onAccept"
            />
            <q-btn round color="negative" icon="call_end" size="lg" @click="onDecline" />
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { computed, watch, ref, onBeforeUnmount } from 'vue'
  import UserAvatarBadge from '@/components/ui/badges/UserAvatarBadge.vue'
  import { useCampaignStore } from '@/stores/campaignStore'
  import { useUserStore } from '@/stores/userStore'

  const props = defineProps({
    modelValue: { type: Boolean, default: false },
    call: { type: Object, default: null }
  })
  const emit = defineEmits(['update:modelValue', 'accept', 'decline'])
  const campaignStore = useCampaignStore()
  const userStore = useUserStore()

  const open = computed({
    get: () => !!props.modelValue,
    set: (v) => emit('update:modelValue', !!v)
  })

  const conversationId = computed(() => String(props.call?.conversationId || '').trim())
  const fromPeerId = computed(() => String(props.call?.fromPeerId || '').trim())
  const inferredFromPeerId = computed(() => {
    if (fromPeerId.value) return fromPeerId.value
    const cid = conversationId.value
    if (!cid.startsWith('direct:')) return ''
    const parts = cid
      .replace(/^direct:/, '')
      .split(':')
      .map((s) => String(s || '').trim())
      .filter(Boolean)
    if (!parts.length) return ''
    const self = String(userStore.userId || '').trim()
    const other = parts.find((id) => id && id !== self)
    return other || parts[0] || ''
  })
  const callerProfile = ref(null)
  const callerProfileLoading = ref(false)
  let callerLookupSeq = 0

  watch(
    () => inferredFromPeerId.value,
    async (uid) => {
      callerProfile.value = null
      callerProfileLoading.value = false
      const key = String(uid || '').trim()
      if (!key) return
      const seq = ++callerLookupSeq
      callerProfileLoading.value = true
      try {
        const profile = await userStore.getUserPublicProfile?.(key)
        if (seq !== callerLookupSeq) return
        callerProfile.value = profile || null
      } catch {
        if (seq !== callerLookupSeq) return
        callerProfile.value = null
      } finally {
        if (seq === callerLookupSeq) callerProfileLoading.value = false
      }
    },
    { immediate: true }
  )

  const fromName = computed(() => {
    const metaName = String(props.call?.meta?.displayName || '').trim()
    if (metaName) return metaName
    const profileName = String(callerProfile.value?.displayName || '').trim()
    if (profileName && profileName.toLowerCase() !== 'unknown user') return profileName
    const uid = inferredFromPeerId.value
    if (uid) return uid
    return callerProfileLoading.value ? 'Loading...' : 'Unknown'
  })
  const isCampaignCall = computed(() => conversationId.value.startsWith('campaign:'))
  const campaignId = computed(() => conversationId.value.replace(/^campaign:/, '').trim())
  const campaignName = ref('')
  const campaignNameLoading = ref(false)
  let campaignLookupSeq = 0

  async function resolveCampaignName(id) {
    const campaignKey = String(id || '').trim()
    if (!campaignKey) return ''
    try {
      const list = Array.isArray(campaignStore.getCampaigns) ? campaignStore.getCampaigns : []
      const cached = list.find((c) => String(c?.id || '').trim() === campaignKey)
      const current =
        String(campaignStore.getCurrentCampaign?.id || '').trim() === campaignKey
          ? campaignStore.getCurrentCampaign
          : null
      const fromCache = cached || current || null
      const cachedName = String(fromCache?.name || fromCache?.title || '').trim()
      if (cachedName) return cachedName
    } catch {}

    try {
      const fetched = await campaignStore.fetchCampaign?.(campaignKey)
      return String(fetched?.name || fetched?.title || '').trim()
    } catch {
      return ''
    }
  }

  watch(
    () => campaignId.value,
    async (id) => {
      campaignName.value = ''
      campaignNameLoading.value = false
      if (!isCampaignCall.value || !id) return
      const seq = ++campaignLookupSeq
      campaignNameLoading.value = true
      const name = await resolveCampaignName(id)
      if (seq !== campaignLookupSeq) return
      campaignName.value = name
      campaignNameLoading.value = false
    },
    { immediate: true }
  )

  const conversationLabel = computed(() => {
    if (isCampaignCall.value) {
      if (campaignName.value) return campaignName.value
      return campaignNameLoading.value ? 'Campaign call' : 'Campaign'
    }
    return conversationId.value || ''
  })
  const avatarSrc = computed(() => {
    const metaPhoto = String(props.call?.meta?.photoURL || '').trim()
    if (metaPhoto) return metaPhoto
    const profilePhoto = String(callerProfile.value?.photoURL || '').trim()
    if (profilePhoto) return profilePhoto
    return ''
  })
  const headerTitle = computed(() => 'Call')

  let ring = null
  const startRing = () => {
    try {
      if (ring) return
      ring = new Audio('/assets/audio/ring.mp3')
      ring.loop = true
      ring.volume = 0.35
      const p = ring.play()
      if (p && typeof p.catch === 'function') p.catch(() => {})
    } catch {}
  }
  const stopRing = () => {
    try {
      ring?.pause?.()
      if (ring) ring.currentTime = 0
    } catch {}
    ring = null
  }

  watch(
    () => open.value,
    (v) => {
      if (v) startRing()
      else stopRing()
    },
    { immediate: true }
  )
  onBeforeUnmount(stopRing)

  function onAccept() {
    stopRing()
    emit('accept', props.call)
    open.value = false
  }
  function onDecline() {
    stopRing()
    emit('decline', props.call)
    open.value = false
  }
</script>

<style scoped></style>
